import { useEffect, useState, useRef, useCallback } from "react";
import {
  doc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "App";
import { apiGetBrandGuidelines } from "services/apiService";

/**
 * Identity fields tracked for dirty-field merging.
 * When a field is edited locally, remote snapshots skip it for 2 seconds.
 */
type IdentityField =
  | "colors"
  | "fonts"
  | "theme_color"
  | "overall_guidelines"
  | "voice_and_tone"
  | "photography_style"
  | "lighting_rules"
  | "composition_rules"
  | "do_not_list"
  | "sources";

const IDENTITY_FIELDS: IdentityField[] = [
  "colors",
  "fonts",
  "theme_color",
  "overall_guidelines",
  "voice_and_tone",
  "photography_style",
  "lighting_rules",
  "composition_rules",
  "do_not_list",
  "sources",
];

const DIRTY_WINDOW_MS = 2000;

export interface BrandGuidelinesRealtimeState {
  guidelines: BrandGuidelines | null;
  assets: BrandAsset[];
  loading: boolean;
  error: string | null;
}

/**
 * Real-time Firestore listener for brand guidelines + assets.
 * Supports dirty-field tracking: locally edited fields are not overwritten
 * by remote snapshots until the dirty window (2s) expires.
 */
export function useBrandGuidelinesRealtime(
  projectId: string | undefined
): BrandGuidelinesRealtimeState & {
  updateField: (field: IdentityField, value: unknown) => void;
  clearDirty: (fields: IdentityField[]) => void;
  refetchAssets: () => Promise<void>;
  patchAsset: (asset: BrandAsset) => void;
  removeAsset: (assetId: string) => void;
} {
  const [state, setState] = useState<BrandGuidelinesRealtimeState>({
    guidelines: null,
    assets: [],
    loading: true,
    error: null,
  });

  // Track dirty fields with timestamps
  const dirtyFields = useRef<Map<IdentityField, number>>(new Map());

  const updateField = useCallback((field: IdentityField, value: unknown) => {
    dirtyFields.current.set(field, Date.now());
    setState((prev) => {
      if (!prev.guidelines) return prev;
      const updated = { ...prev.guidelines, [field]: value };
      return { ...prev, guidelines: updated };
    });
  }, []);

  const clearDirty = useCallback((fields: IdentityField[]) => {
    fields.forEach((f) => dirtyFields.current.delete(f));
  }, []);

  // Assets need signed URLs that Firestore doesn't store, so they're
  // fetched via REST rather than subscribed. Used for initial load; for
  // individual mutations prefer patchAsset/removeAsset (no network round
  // trip, immune to the Firestore snapshot race).
  const refetchAssets = useCallback(async () => {
    if (!projectId) return;
    try {
      const bg = await apiGetBrandGuidelines(projectId);
      const assets = bg.assets || [];
      setState((prev) => ({
        ...prev,
        assets,
        guidelines: prev.guidelines ? { ...prev.guidelines, assets } : prev.guidelines,
      }));
    } catch {
      // Silently fail — identity from Firestore is the priority
    }
  }, [projectId]);

  // Apply a single asset returned by create/update. Avoids both the
  // snapshot-listener race (which resets guidelines.assets from prev.assets)
  // and an extra GET round-trip after every mutation.
  const patchAsset = useCallback((asset: BrandAsset) => {
    setState((prev) => {
      const exists = prev.assets.some((a) => a.id === asset.id);
      const nextAssets = exists
        ? prev.assets.map((a) => (a.id === asset.id ? asset : a))
        : [...prev.assets, asset];
      return {
        ...prev,
        assets: nextAssets,
        guidelines: prev.guidelines
          ? { ...prev.guidelines, assets: nextAssets }
          : prev.guidelines,
      };
    });
  }, []);

  const removeAsset = useCallback((assetId: string) => {
    setState((prev) => {
      const nextAssets = prev.assets.filter((a) => a.id !== assetId);
      return {
        ...prev,
        assets: nextAssets,
        guidelines: prev.guidelines
          ? { ...prev.guidelines, assets: nextAssets }
          : prev.guidelines,
      };
    });
  }, []);

  useEffect(() => {
    dirtyFields.current.clear();

    if (!projectId) {
      setState({ guidelines: null, assets: [], loading: false, error: null });
      return;
    }

    setState({ guidelines: null, assets: [], loading: true, error: null });

    const guidelinesDocRef = doc(db, "projects", projectId, "_meta", "brand_guidelines");

    refetchAssets();

    // Listen to guidelines document (identity fields only)
    const unsubGuidelines = onSnapshot(
      guidelinesDocRef,
      (snap) => {
        if (!snap.exists()) {
          const empty: BrandGuidelines = {
            project_id: projectId,
            colors: [],
            fonts: [],
            sources: [],
            assets: [],
          };
          setState((prev) => ({ ...prev, guidelines: empty, loading: false }));
          return;
        }

        const remote = snap.data();
        const now = Date.now();

        setState((prev) => {
          // Use previous guidelines as base so dirty fields keep their local value
          const base = prev.guidelines ?? {
            project_id: projectId,
            colors: [],
            fonts: [],
            sources: [],
            assets: prev.assets,
          };

          const merged: Record<string, unknown> = { ...base };
          // Merge remote fields, skipping dirty ones
          for (const field of IDENTITY_FIELDS) {
            const dirtyAt = dirtyFields.current.get(field);
            if (dirtyAt && now - dirtyAt < DIRTY_WINDOW_MS) {
              // Field is dirty — keep local value
              continue;
            }
            if (remote[field] !== undefined) {
              merged[field] = remote[field];
            }
          }

          // Always merge non-identity fields from remote
          merged.project_id = projectId;
          merged.project_name = remote.project_name ?? base.project_name;
          merged.updated_at = remote.updated_at instanceof Timestamp
            ? remote.updated_at.toDate().toISOString()
            : remote.updated_at ?? base.updated_at;
          merged.training_status = remote.training_status ?? base.training_status;
          merged.training_last_run_at = remote.training_last_run_at instanceof Timestamp
            ? remote.training_last_run_at.toDate().toISOString()
            : remote.training_last_run_at ?? base.training_last_run_at ?? null;
          merged.training_last_exemplars_count = remote.training_last_exemplars_count ?? base.training_last_exemplars_count ?? 0;
          merged.text_content_conventions = remote.text_content_conventions ?? (base as Record<string, unknown>).text_content_conventions;
          merged.assets = prev.assets; // Assets come from REST fetch

          const result = merged as unknown as BrandGuidelines;
          return { ...prev, guidelines: result, loading: false };
        });
      },
      (err) => {
        setState((prev) => ({ ...prev, error: err.message, loading: false }));
      }
    );

    return () => {
      unsubGuidelines();
    };
  }, [projectId, refetchAssets]);

  return { ...state, updateField, clearDirty, refetchAssets, patchAsset, removeAsset };
}
